import React, { useRef, useState, useEffect } from 'react';
import { MessageSquare, RotateCcw, SquareStack } from 'lucide-react';
import { useInView, useReducedMotion } from 'framer-motion';
import { DemoStage } from './DemoChrome';
import { DraftAssetCard } from './DraftAssetCard';
import { DEMO_LIBRARY_VIDEO_THUMB } from './demoStockImages';
import './marketingDemos.css';

const INSTAGRAM_DRAFT = {
  platformName: 'Instagram',
  platformColor: '#9333ea',
  logoPath: '/platform-logos/instagram-logo.png',
  fallbackIcon: '🎬',
};

const FEEDBACK_TEXT = 'Tighten the hook in the first three seconds.';

const PHASES = [
  {
    id: 'in_review',
    statusChip: 'Internal: In review',
    statusTone: 'review',
    showNote: true,
    highlight: null,
  },
  {
    id: 'request_changes',
    statusChip: 'Internal: In review',
    statusTone: 'review',
    showNote: true,
    highlight: 'request',
  },
  {
    id: 'changes_requested',
    statusChip: 'Internal: Changes requested',
    statusTone: 'changes',
    showNote: true,
    highlight: null,
  },
  {
    id: 'new_revision',
    statusChip: 'Internal: Changes requested',
    statusTone: 'changes',
    showNote: true,
    highlight: 'revision',
  },
  {
    id: 'revision_created',
    statusChip: 'Internal: Draft',
    statusTone: 'draft',
    showNote: false,
    showRevisionBadge: true,
    highlight: null,
  },
];

function wait(durationMs) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, durationMs);
  });
}

export default function RevisionsDemo() {
  const reduceMotion = useReducedMotion();
  const rootRef = useRef(null);
  const notesListRef = useRef(null);
  const isInView = useInView(rootRef, { amount: 0.3, once: false });
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [composeStep, setComposeStep] = useState('idle');
  const [typedChars, setTypedChars] = useState(0);
  const [notePosted, setNotePosted] = useState(false);

  const phase = PHASES[phaseIndex];
  const dimNewRevision =
    phase.id === 'in_review' || phase.id === 'request_changes';
  const showFeedbackPanel = notePosted && phase.showNote;
  const isTypingStep = composeStep === 'type';
  const isSendStep = composeStep === 'send';
  const isComposeActive = isTypingStep || isSendStep;
  const composeBody = isComposeActive
    ? FEEDBACK_TEXT.slice(0, isSendStep ? FEEDBACK_TEXT.length : typedChars)
    : '';

  useEffect(() => {
    if (!isInView) {
      return undefined;
    }

    let cancelled = false;

    const runLoop = async () => {
      while (!cancelled) {
        setPhaseIndex(0);
        setComposeStep('idle');
        setTypedChars(0);
        setNotePosted(false);

        await wait(reduceMotion ? 450 : 700);
        if (cancelled) return;

        setComposeStep('type');
        const charDelayMs = reduceMotion ? 0 : 38;

        for (let charCount = 0; charCount <= FEEDBACK_TEXT.length; charCount += 1) {
          if (cancelled) return;
          setTypedChars(charCount);
          if (charCount < FEEDBACK_TEXT.length) {
            await wait(charDelayMs);
          }
        }

        if (cancelled) return;
        setComposeStep('send');
        await wait(reduceMotion ? 350 : 550);

        if (cancelled) return;
        setNotePosted(true);
        setComposeStep('idle');
        setTypedChars(0);
        await wait(reduceMotion ? 500 : 800);

        for (let index = 1; index < PHASES.length; index += 1) {
          if (cancelled) return;
          setPhaseIndex(index);
          await wait(reduceMotion ? 900 : 1200);
        }

        if (cancelled) return;
        await wait(reduceMotion ? 500 : 900);
      }
    };

    runLoop();

    return () => {
      cancelled = true;
    };
  }, [isInView, reduceMotion]);

  useEffect(() => {
    const notesListElement = notesListRef.current;
    if (!notesListElement) {
      return;
    }
    notesListElement.scrollTop = notesListElement.scrollHeight;
  }, [notePosted, phaseIndex]);

  return (
    <div ref={rootRef}>
      <DemoStage visualClassName="mkt-demo-visual--revisions">
        <div className="mkt-revision-demo mkt-revision-demo--split">
          <div className="mkt-revision-asset-column">
            <div className="mkt-revision-card-stack">
              {phase.showRevisionBadge ? (
                <span className="mkt-revision-version-badge">
                  <span className="mkt-revision-version-badge-fold" aria-hidden />
                  Revision 2
                </span>
              ) : null}

              <DraftAssetCard
                title="Q2 product reel"
                platformName={INSTAGRAM_DRAFT.platformName}
                platformColor={INSTAGRAM_DRAFT.platformColor}
                logoPath={INSTAGRAM_DRAFT.logoPath}
                fallbackIcon={INSTAGRAM_DRAFT.fallbackIcon}
                thumbnailUrl={DEMO_LIBRARY_VIDEO_THUMB}
                thumbnailLoading="eager"
                className="mkt-revision-draft-card"
              />

              {phase.statusChip ? (
                <span
                  className={`mkt-revision-status-chip mkt-revision-status-chip--${phase.statusTone}`}
                >
                  <span className="mkt-revision-status-chip-dot" aria-hidden />
                  {phase.statusChip}
                </span>
              ) : null}
            </div>

            <div className="mkt-revision-actions">
              <div
                className={`mkt-revision-btn mkt-revision-btn--changes mkt-revision-btn--auto${
                  phase.highlight === 'request' ? ' mkt-revision-btn--highlight' : ''
                }`}
              >
                <RotateCcw className="ui-icon-md" aria-hidden />
                Request changes
              </div>
              <div
                className={`mkt-revision-btn mkt-revision-btn--new mkt-revision-btn--auto${
                  phase.highlight === 'revision' ? ' mkt-revision-btn--highlight' : ''
                }${dimNewRevision ? ' mkt-revision-btn--dim' : ''}`}
              >
                <SquareStack className="ui-icon-md" aria-hidden />
                New revision
              </div>
            </div>
          </div>

          <aside
            className={`mkt-revision-notes-panel${
              showFeedbackPanel ? ' mkt-revision-notes-panel--has-note' : ''
            }${phase.highlight === 'request' ? ' mkt-revision-notes-panel--highlight' : ''}`}
            aria-label="Draft feedback"
          >
            <div className="mkt-revision-notes-tabs" aria-hidden>
              <span className="mkt-revision-notes-tab mkt-revision-notes-tab--active">
                Feedback
              </span>
              <span className="mkt-revision-notes-tab">Activity</span>
            </div>

            <div ref={notesListRef} className="mkt-revision-notes-scroll">
              {showFeedbackPanel ? (
                <div className="mkt-revision-feedback-note">
                  <MessageSquare className="ui-icon-md mkt-revision-feedback-icon" aria-hidden />
                  <p className="mkt-revision-feedback-text">{FEEDBACK_TEXT}</p>
                </div>
              ) : (
                <p className="mkt-revision-notes-empty">Feedback appears here during review.</p>
              )}
            </div>

            <div className="mkt-revision-notes-compose">
              <span className="mkt-revision-notes-compose-label">Add feedback</span>
              <div
                className={`mkt-revision-notes-compose-field${
                  isComposeActive ? ' mkt-revision-notes-compose-field--active' : ''
                }${isTypingStep || isSendStep ? ' mkt-revision-notes-compose-field--typing' : ''}`}
              >
                {isComposeActive ? (
                  <>
                    <span className="mkt-revision-compose-body">{composeBody}</span>
                    {isTypingStep && typedChars < FEEDBACK_TEXT.length ? (
                      <span className="mkt-revision-compose-cursor" aria-hidden />
                    ) : null}
                  </>
                ) : (
                  'Leave a note on this draft…'
                )}
              </div>
              <div className="mkt-revision-notes-compose-actions">
                <button
                  type="button"
                  className={`mkt-revision-compose-send-btn${
                    isSendStep ? ' mkt-revision-compose-send-btn--highlight' : ''
                  }`}
                  tabIndex={-1}
                >
                  Send note
                </button>
              </div>
            </div>
          </aside>
        </div>
      </DemoStage>
    </div>
  );
}
